import { amber, green } from '@mui/material/colors';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import SnackbarContent from '@mui/material/SnackbarContent';
import { makeStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Info,
  X
} from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { hideMessage } from 'src/store/actions/snackBarActions';
import {
  selectSnackbarOptions,
  selectSnackbarState
} from 'src/store/selectors/snackBarSelectors';
import { SnackBarVariants } from 'src/utils/constants/common';

const useStyles = makeStyles()(theme => ({
  root: {},
  success: {
    backgroundColor: green[600],
    color: '#FFFFFF'
  },
  error: {
    backgroundColor: theme.palette.error.dark,
    color: theme.palette.getContrastText(theme.palette.error.dark)
  },
  info: {
    backgroundColor: '#2196F3',
    color: 'black'
  },
  warning: {
    backgroundColor: amber[600],
    color: 'black'
  },
  message: {
    marginLeft: theme.spacing()
  },
  container: {
    display: 'flex',
    alignItems: 'center'
  }
}));

const variantIcon = {
  success: <CheckCircle />,
  warning: <AlertTriangle />,
  error: <AlertCircle />,
  info: <Info />
};

function SnackbarMessage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const state = useSelector(selectSnackbarState);
  const options = useSelector(selectSnackbarOptions);

  options.variant = options.variant || 'info';

  const { classes } = useStyles();
  const handleOnClose = useCallback(() => {
    dispatch(hideMessage());
  }, [dispatch]);

  const messageComponent = useMemo(() => {
    // Check if message already contains a full translation path (contains a dot)
    const isFullPath = options.message && options.message.includes('.');
    let translationKey = options.message;

    if (!isFullPath) {
      if (options.variant === SnackBarVariants.ERROR) {
        translationKey = `ERRORS.${options.message}`;
      } else if (options.variant === SnackBarVariants.SUCCESS) {
        translationKey = `SUCCESS.${options.message}`;
      } else if (options.variant === SnackBarVariants.WARNING) {
        translationKey = `WARNING.${options.message}`;
      } else {
        translationKey = `INFO.${options.message}`;
      }
    }

    return (
      <div className={classes.container}>
        {variantIcon[options.variant]}
        <Typography className={classes.message}>
          {t(translationKey, {
            defaultValue: options.message
          })}
        </Typography>
      </div>
    );
  }, [classes, options, t]);

  return (
    <Snackbar
      {...options}
      anchorOrigin={
        options.anchorOrigin || { vertical: 'top', horizontal: 'center' }
      }
      open={state}
      onClose={handleOnClose}
      classes={{
        root: classes.root
      }}
      ContentProps={{
        variant: 'body2',
        headlineMapping: {
          body1: 'div',
          body2: 'div'
        }
      }}
    >
      <SnackbarContent
        className={clsx(classes[options.variant])}
        message={messageComponent}
        action={[
          <IconButton
            key="close"
            aria-label="Close"
            color="inherit"
            onClick={handleOnClose}
          >
            <X />
          </IconButton>
        ]}
      />
    </Snackbar>
  );
}

export default React.memo(SnackbarMessage);
